import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Custom hook that scrolls to top when the route changes
 * @param behavior - Scroll behavior ('auto' | 'smooth')
 */
export const useScrollToTop = (behavior: ScrollBehavior = "auto") => {
  const location = useLocation();

  useEffect(() => {
    // Scroll to top of the main content area
    const mainContent = document.querySelector("main");
    if (mainContent) {
      mainContent.scrollTo({
        top: 0,
        left: 0,
        behavior,
      });
    } else {
      // Fallback to window scroll if main element not found
      window.scrollTo({
        top: 0,
        left: 0,
        behavior,
      });
    }
  }, [location.pathname, behavior]);
};

/**
 * Utility function to scroll to top programmatically
 * @param behavior - Scroll behavior ('auto' | 'smooth')
 * @param target - Target element to scroll (defaults to main content or window)
 */
export const scrollToTop = (
  behavior: ScrollBehavior = "auto",
  target?: HTMLElement
) => {
  if (target) {
    target.scrollTo({
      top: 0,
      left: 0,
      behavior,
    });
  } else {
    // Try to scroll main content first, then fallback to window
    const mainContent = document.querySelector("main");
    if (mainContent) {
      mainContent.scrollTo({
        top: 0,
        left: 0,
        behavior,
      });
    } else {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior,
      });
    }
  }
};

/**
 * Utility function to scroll to top with smooth animation
 */
export const scrollToTopSmooth = () => {
  scrollToTop("smooth");
};

/**
 * Utility function to scroll to top instantly
 */
export const scrollToTopInstant = () => {
  scrollToTop("auto");
};
